import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Target, TrendingUp, Zap, Hash, MessageSquare, ArrowRight, RefreshCw, ChevronRight, ExternalLink, Clock, BarChart3, Bell, Trash2 } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { useAuth } from '../lib/auth';
import api from '../lib/api';
import { toast } from 'sonner';

function IntentBadge({ score }) {
  let cls = 'intent-low';
  let ring = 'ring-gray-200';
  if (score >= 70) { cls = 'intent-high'; ring = 'ring-orange-200'; }
  else if (score >= 40) { cls = 'intent-medium'; ring = 'ring-amber-200'; }
  return (
    <span className={`${cls} text-xs font-bold px-2.5 py-1 rounded-full inline-block tabular-nums ring-1 ${ring}`} style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
      {score}
    </span>
  );
}

function StatusBadge({ status }) {
  const cls = `status-${status}`;
  return <Badge variant="outline" className={`text-[10px] border ${cls} capitalize font-medium`}>{status}</Badge>;
}

function StatCard({ icon: Icon, label, value, color, delay, subtext }) {
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}>
      <Card className="p-5 rounded-xl border-[#E6E8EE] bg-white shadow-[0_1px_3px_rgba(16,24,40,0.04)] hover:shadow-md transition-shadow">
        <div className="flex items-start justify-between mb-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${color}12` }}>
            <Icon className="w-5 h-5" style={{ color }} />
          </div>
        </div>
        <p className="text-2xl font-extrabold tabular-nums" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{value}</p>
        <p className="text-xs text-[#5B667A] font-medium mt-0.5">{label}</p>
        {subtext && <p className="text-[10px] text-[#9ca3af] mt-1">{subtext}</p>}
      </Card>
    </motion.div>
  );
}

export default function Dashboard() {
  const { activeBusiness, refreshBusinesses } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [keywords, setKeywords] = useState([]);
  const [subreddits, setSubreddits] = useState([]);
  const [notifSettings, setNotifSettings] = useState(null);
  const [lastScanLogs, setLastScanLogs] = useState([]);

  const fetchData = useCallback(async () => {
    if (!activeBusiness) return;
    setLoading(true);
    try {
      const [leadsData, kwData, subData, notif, logs] = await Promise.all([
        api.getLeads(activeBusiness._id, { sort: 'intent_desc', limit: 5 }),
        api.getKeywords(activeBusiness._id),
        api.getSubreddits(activeBusiness._id),
        api.getNotificationSettings(activeBusiness._id).catch(() => null),
        api.getScanLogs(activeBusiness._id, 3).catch(() => []),
      ]);
      setLeads(leadsData.leads || []);
      setTotal(leadsData.total || 0);
      setKeywords(kwData || []);
      setSubreddits(subData || []);
      setNotifSettings(notif);
      setLastScanLogs(logs || []);
    } catch {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [activeBusiness]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRefresh = async () => {
    if (!activeBusiness) return;
    setRefreshing(true);
    try {
      const result = await api.refreshLeads(activeBusiness._id);
      toast.success(`Found ${result.new_leads} new leads!`);
      fetchData();
    } catch (err) {
      toast.error(err.message || 'Refresh failed');
    } finally {
      setRefreshing(false);
    }
  };

  const highIntentCount = leads.length > 0 ? leads.filter(l => l.intent_score >= 70).length : 0;
  const newCount = leads.length > 0 ? leads.filter(l => l.status === 'new').length : 0;

  if (!activeBusiness) {
    return (
      <DashboardLayout title="Dashboard">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="flex flex-col items-center justify-center py-20">
          <div className="w-20 h-20 rounded-3xl flex items-center justify-center mb-6" style={{ background: 'linear-gradient(135deg, #FFF2EA, #FFE8DE)' }}>
            <Target className="w-9 h-9 text-[#FF4500]" />
          </div>
          <h2 className="text-xl font-bold mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>No business set up yet</h2>
          <p className="text-sm text-[#5B667A] mb-6 max-w-sm text-center">Add your first business to start discovering high-intent Reddit leads.</p>
          <Button onClick={() => navigate('/onboard')} className="rounded-xl font-semibold cta-btn shadow-lg" style={{ background: 'linear-gradient(135deg, #FF6A2A 0%, #FF4500 100%)' }}>
            Add Your First Business
          </Button>
        </motion.div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Dashboard">
      {/* Stats Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={Target} label="Total Leads" value={loading ? '—' : total} color="#FF4500" delay={0} subtext={`${newCount} new leads`} />
        <StatCard icon={Zap} label="High Intent (70+)" value={loading ? '—' : highIntentCount} color="#D97706" delay={0.05} subtext="Highest priority" />
        <StatCard icon={Hash} label="Active Keywords" value={loading ? '—' : keywords.filter(k => k.enabled).length} color="#2563EB" delay={0.1} subtext={`${keywords.length} total`} />
        <StatCard icon={MessageSquare} label="Monitored Subreddits" value={loading ? '—' : subreddits.filter(s => s.enabled).length} color="#16A34A" delay={0.15} subtext={`${subreddits.length} total`} />
      </div>

      <div className="grid xl:grid-cols-[1fr_300px] gap-5 min-w-0 overflow-hidden">
        {/* Top Leads */}
        <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <Card className="rounded-2xl border-[#E6E8EE] bg-white overflow-hidden shadow-[0_1px_3px_rgba(16,24,40,0.04)]">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#E6E8EE]">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-[#FF4500]" />
                <h3 className="text-sm font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Top Opportunities</h3>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" className="h-8 rounded-lg text-xs" onClick={handleRefresh} disabled={refreshing} data-testid="refresh-leads-button">
                  <RefreshCw className={`w-3 h-3 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
                  {refreshing ? 'Scanning...' : 'Refresh'}
                </Button>
                <Button variant="ghost" size="sm" className="h-8 rounded-lg text-xs text-[#FF4500] hover:bg-[#FFF2EA]" onClick={() => navigate('/leads')}>
                  View All <ChevronRight className="w-3 h-3 ml-0.5" />
                </Button>
              </div>
            </div>

            {loading ? (
              <div className="p-4 space-y-3">
                {[1,2,3,4,5].map(i => (
                  <div key={i} className="flex items-center gap-3"><Skeleton className="h-4 flex-1" /><Skeleton className="h-6 w-10 rounded-full" /><Skeleton className="h-5 w-14 rounded-full" /></div>
                ))}
              </div>
            ) : leads.length === 0 ? (
              <div className="p-10 text-center">
                <Target className="w-8 h-8 text-[#d1d5db] mx-auto mb-2" />
                <p className="text-sm text-[#5B667A]">No leads yet. Add keywords and subreddits, then refresh.</p>
              </div>
            ) : (
              <div>
                {leads.filter(l => !l.blurred).slice(0, 3).map((lead, idx) => (
                  <motion.div
                    key={lead._id}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.25 + idx * 0.04 }}
                    className="lead-row flex items-center gap-4 px-5 py-3.5 border-b border-[#E6E8EE]/50 last:border-b-0 group min-w-0"
                    onClick={() => navigate(`/leads/${lead._id}`)}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#0B0F17] truncate group-hover:text-[#FF4500] transition-colors">{lead.title}</p>
                      <span className="text-[11px] text-[#5B667A] bg-[#F6F7F9] px-2 py-0.5 rounded-full mt-1 inline-block">r/{lead.subreddit}</span>
                    </div>
                    <IntentBadge score={lead.intent_score} />
                    <StatusBadge status={lead.status} />
                    <ChevronRight className="w-4 h-4 text-[#d1d5db] group-hover:text-[#FF4500] transition-colors flex-shrink-0" />
                  </motion.div>
                ))}
              </div>
            )}
          </Card>
        </motion.div>

        {/* Right sidebar — Quick Actions & Activity */}
        <div className="space-y-5">
          {/* Quick Actions */}
          <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <Card className="p-5 rounded-2xl border-[#E6E8EE] bg-white shadow-[0_1px_3px_rgba(16,24,40,0.04)]">
              <h3 className="text-sm font-bold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Quick Actions</h3>
              <div className="space-y-2">
                <Button variant="outline" className="w-full justify-start h-10 rounded-xl text-sm hover:bg-[#FFF2EA] hover:text-[#FF4500] hover:border-[#FF4500]/20" onClick={() => navigate('/leads')}>
                  <Target className="w-4 h-4 mr-2.5" /> Browse All Leads
                </Button>
                <Button variant="outline" className="w-full justify-start h-10 rounded-xl text-sm hover:bg-[#FFF2EA] hover:text-[#FF4500] hover:border-[#FF4500]/20" onClick={() => navigate('/keywords')}>
                  <Hash className="w-4 h-4 mr-2.5" /> Manage Keywords
                </Button>
                <Button variant="outline" className="w-full justify-start h-10 rounded-xl text-sm hover:bg-[#FFF2EA] hover:text-[#FF4500] hover:border-[#FF4500]/20" onClick={() => navigate('/subreddits')}>
                  <MessageSquare className="w-4 h-4 mr-2.5" /> Manage Subreddits
                </Button>
                <Button variant="outline" className="w-full justify-start h-10 rounded-xl text-sm hover:bg-[#FFF2EA] hover:text-[#FF4500] hover:border-[#FF4500]/20" onClick={() => navigate('/onboard')}>
                  <BarChart3 className="w-4 h-4 mr-2.5" /> Add New Business
                </Button>
              </div>
            </Card>
          </motion.div>

          {/* Business Info */}
          <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
            <Card className="p-5 rounded-2xl border-[#E6E8EE] bg-white shadow-[0_1px_3px_rgba(16,24,40,0.04)]">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Active Business</h3>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-[#9ca3af] hover:text-red-500 hover:bg-red-50" data-testid="delete-business-button">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete "{activeBusiness?.name}"?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This will permanently delete this business and all its leads, keywords, subreddits, and scan history. This action cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        className="bg-red-500 hover:bg-red-600 text-white"
                        onClick={async () => {
                          try {
                            await api.deleteBusiness(activeBusiness._id);
                            toast.success('Business deleted');
                            await refreshBusinesses();
                            navigate('/onboard');
                          } catch (err) {
                            toast.error(err.message || 'Failed to delete');
                          }
                        }}
                        data-testid="confirm-delete-business"
                      >
                        Delete Business
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
              <p className="text-base font-semibold text-[#0B0F17]">{activeBusiness?.name}</p>
              {activeBusiness?.website_url && (
                <a href={activeBusiness.website_url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#FF4500] hover:underline flex items-center gap-1 mt-1">
                  {activeBusiness.website_url} <ExternalLink className="w-3 h-3" />
                </a>
              )}
              {activeBusiness?.product_description && (
                <p className="text-xs text-[#5B667A] mt-2 leading-relaxed">{activeBusiness.product_description}</p>
              )}
            </Card>
          </motion.div>

          {/* Monitoring Status */}
          <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
            <Card className="p-5 rounded-2xl border-[#E6E8EE] bg-white shadow-[0_1px_3px_rgba(16,24,40,0.04)]">
              <h3 className="text-sm font-bold mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Monitoring</h3>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-[#5B667A]">Keywords</span>
                  <span className="text-xs font-semibold">{keywords.filter(k => k.enabled).length}/{keywords.length}</span>
                </div>
                <div className="w-full h-1.5 bg-[#F6F7F9] rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[#FF4500] to-[#FF6A2A] rounded-full" style={{ width: `${keywords.length > 0 ? (keywords.filter(k => k.enabled).length / keywords.length) * 100 : 0}%` }} />
                </div>
                <div className="flex items-center justify-between mt-3">
                  <span className="text-xs text-[#5B667A]">Subreddits</span>
                  <span className="text-xs font-semibold">{subreddits.filter(s => s.enabled).length}/{subreddits.length}</span>
                </div>
                <div className="w-full h-1.5 bg-[#F6F7F9] rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[#16A34A] to-[#22c55e] rounded-full" style={{ width: `${subreddits.length > 0 ? (subreddits.filter(s => s.enabled).length / subreddits.length) * 100 : 0}%` }} />
                </div>
              </div>
            </Card>
          </motion.div>

          {/* Auto-Scan Status */}
          <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}>
            <Card className="p-5 rounded-2xl border-[#E6E8EE] bg-white shadow-[0_1px_3px_rgba(16,24,40,0.04)]">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Auto-Scanner</h3>
                {notifSettings?.auto_scan_enabled !== false ? (
                  <div className="flex items-center gap-1.5">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#16A34A] opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-[#16A34A]"></span>
                    </span>
                    <span className="text-[10px] font-semibold text-[#16A34A]">Active</span>
                  </div>
                ) : (
                  <Badge variant="secondary" className="text-[10px]">Paused</Badge>
                )}
              </div>
              <div className="space-y-2">
                {notifSettings?.last_auto_scan_iso && (
                  <div className="flex items-center gap-2 text-xs text-[#5B667A]">
                    <Clock className="w-3 h-3 flex-shrink-0" />
                    <span>Last scan: {new Date(notifSettings.last_auto_scan_iso).toLocaleString()}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-xs text-[#5B667A]">
                  <RefreshCw className="w-3 h-3 flex-shrink-0" />
                  <span>Every {notifSettings?.scan_interval_minutes || 30} min</span>
                </div>
                {notifSettings?.slack_webhook_url && (
                  <div className="flex items-center gap-2 text-xs text-[#5B667A]">
                    <MessageSquare className="w-3 h-3 flex-shrink-0" />
                    <span>Slack notifications on</span>
                  </div>
                )}
              </div>
              {lastScanLogs.length > 0 && (
                <div className="mt-3 pt-3 border-t border-[#E6E8EE]">
                  <p className="text-[10px] font-semibold text-[#9ca3af] uppercase tracking-wider mb-2">Recent scans</p>
                  {lastScanLogs.map((log, i) => (
                    <div key={log._id || i} className="flex items-center justify-between py-1">
                      <span className="text-[11px] text-[#5B667A]">
                        {log.new_leads > 0 ? `+${log.new_leads} leads` : 'No new'}
                      </span>
                      <span className="text-[10px] text-[#9ca3af]">
                        {log.timestamp ? new Date(log.timestamp).toLocaleTimeString() : ''}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="w-full mt-3 text-xs text-[#5B667A] hover:text-[#FF4500] hover:bg-[#FFF2EA]"
                onClick={() => navigate('/settings')}
              >
                Configure scanning & notifications
              </Button>
            </Card>
          </motion.div>
        </div>
      </div>
    </DashboardLayout>
  );
}
